/**
 * #1433: Trader Detail's Kalshi section reads. A non-2xx, network-failed or
 * unparseable read is unavailable, never an empty list; a valid empty array
 * stays authoritative and clears the section. The panel rules themselves are
 * Org Detail's (#1386); only the decoding and the wording are this page's.
 */
import type { OrgReadPanel } from '../OrgDetail/orgReadPanel';

/**
 * The rows of a Kalshi manager read, or null when the read cannot be trusted:
 * a non-2xx, a body that did not parse (`null`), no array in the first present
 * `containers` key, or an element that is not an object. A null element would
 * throw during render and take the whole view down to the route's error screen.
 *
 * Only the shape is checked, never field values (#435). Not the account
 * store's `parse*Snapshot`: those normalise rows and are stricter than this page
 * has been (`parseOrdersSnapshot` refuses an order without an id, or `results`),
 * and this page renders the raw rows.
 */
export function decodeKalshiRows<T>(ok: boolean, payload: unknown, containers: readonly string[]): T[] | null {
  if (!ok || !payload || typeof payload !== 'object') return null;
  const body = payload as Record<string, unknown>;
  const rows = containers.map((key) => body[key]).find((value) => value != null);
  if (!Array.isArray(rows)) return null;
  if (!rows.every((row) => row !== null && typeof row === 'object' && !Array.isArray(row))) return null;
  return rows as T[];
}

/** No read of this section has landed yet for this trader. */
export const kalshiPanelUnread = (panel: OrgReadPanel<unknown>): boolean =>
  !panel.confirmed && panel.disclosure === 'none';

/** The latest read of this section landed and was valid. */
export const kalshiPanelCurrent = (panel: OrgReadPanel<unknown>): boolean =>
  panel.confirmed && panel.disclosure === 'none';

/** What a section says about its latest read, or null when there is nothing to say. */
export function kalshiReadNote(panel: OrgReadPanel<unknown>, label: string): string | null {
  if (panel.disclosure === 'unavailable') return `Kalshi ${label} did not load.`;
  if (panel.disclosure !== 'retained') return null;
  return panel.rows.length > 0
    ? `Kalshi ${label} did not refresh. Showing the last confirmed ${label}.`
    : `Kalshi ${label} did not refresh. The last confirmed read had none.`;
}
